import { BOOK_PATH, BOOKS_PATH, MOVED_BOOK_PATH } from '@app/shared'
import {
  type AnyRoute,
  createRoute,
  type RouteComponent,
  redirect,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import type { ComponentType, LazyExoticComponent } from 'react'
import { listSearch } from './list-search'

/** The two screens a book's routes draw, handed in by whoever mounts them. */
type BookProps = { bookId: string; beside?: string }

export type BookPages = {
  Shelf: RouteComponent
  Book: ComponentType<BookProps> | LazyExoticComponent<ComponentType<BookProps>>
  /** Answers not found where the shelf does not belong. */
  guard?: () => void
}

/**
 * Every address a book answers at: the shelf, one book, and the two that mail
 * already sent carries, each of which sends the reader to the book's address.
 *
 * **One set, mounted whole.** The addresses in circulation are returned by the
 * same call as the ones they redirect to, so a tree that holds a book's routes
 * holds the redirects too, and neither can be left out without the other. The
 * paths are the templates the API composes a mailed address from, which is what
 * keeps the two sides on one spelling.
 *
 * Built against whichever root and screens are handed over, so the routes a
 * test resolves an address against are the routes the app mounts rather than a
 * copy of them.
 *
 * **A redirect carries everything after the `?`.** A rollup, an invitation and
 * an offer each wrote a link, and mail cannot be recalled: `beside=history` is
 * what makes a rollup's link open on the history rather than on the top of the
 * book.
 */
export function bookRoutes<TRoot extends AnyRoute>(
  root: TRoot,
  pages: BookPages,
) {
  const shelf = createRoute({
    getParentRoute: () => root,
    path: BOOKS_PATH,
    beforeLoad: pages.guard,
    component: pages.Shelf,
    validateSearch: listSearch,
  })

  /* A book is read at its own address: one held in a list's state cannot be
     linked to, and a reload puts you back on the list. */
  const book = createRoute({
    getParentRoute: () => root,
    path: BOOK_PATH,
    beforeLoad: pages.guard,
    /* `beside` names a view of the band to open on arrival, so a book can be
       linked to WITH something beside it — which is what lets a rollup hand
       somebody its history rather than the top of the book and a hunt.
       Validated like any other list search: an unknown name opens nothing
       rather than failing, because a link outlives the view it named. */
    validateSearch: listSearch,
    component: function BookRoute() {
      /* Read by the route's own address: inside a function generic over its
         root, the route object cannot say which tree it is part of. */
      const { id } = useParams({ from: BOOK_PATH })
      const { beside } = useSearch({ from: BOOK_PATH })
      return <pages.Book bookId={id} beside={beside} />
    },
  })

  const movedBook = createRoute({
    getParentRoute: () => root,
    path: MOVED_BOOK_PATH,
    beforeLoad: ({ params, location }) => {
      throw redirect({
        to: BOOK_PATH,
        params: { id: params.id },
        search: location.search,
      })
    },
  })

  return [shelf, book, movedBook] as const
}
